// Action suffixes that only make sense once the base "view" action for the
// same resource is granted — stops an admin handing out create/update/
// delete/manage access to a resource nobody can even see.
//
// schedule/reschedule/cancel/reassign/queue_manage/view_link are the
// Interview Scheduling module's action names (server/app/core/permissions.py)
// — added because they were falling through this rule entirely: a role could
// be granted "Schedule Interview" with "View" left unchecked, and the
// checkbox UI gave no indication that was an inconsistent state (the backend
// itself doesn't require view for these — its `_view` gate is "any of"
// view/schedule/reschedule/cancel/manage, deliberately, so an action
// permission alone still grants read access — but the *role editor* should
// still guide admins toward granting view alongside an action, not leave it
// implicit).
// "review" is here for the same reason: reviewing the compliance queue is an
// action on records a role must be able to see. It is a vocabulary-wide
// statement, not a compliance-specific one — today COMPLIANCE_REVIEW is the
// only code with this action, so the next resource to grow a "review" action
// inherits the rule.
// "master_manage" likewise: editing a country document set is an action on a
// checklist, and a role that cannot see compliance has no business holding it.
//
// NOT here: "audit_view". The compliance audit page is gated on
// COMPLIANCE_AUDIT_VIEW *alone* (compliance/router.py:1530), deliberately —
// it reaches audit rows about candidates a recruiter cannot see through any
// job, so it is granted on purpose rather than falling out of View. Requiring
// View first would be this editor inventing a rule the server does not have.
// Any future "<x>_view" action is a base right for the same reason, so the
// list holds actions, never a bare "view"-suffixed name.
const VIEW_DEPENDENT_ACTIONS = [
    "view_all", "create", "update", "delete", "assign", "manage", "review",
    "master_manage",
    "schedule", "reschedule", "cancel", "reassign", "queue_manage", "view_link",
];

// One-way implications: ticking the key grants every code in its list, because
// the stronger-sounding permission opens no route without them.
//
// `compliance.master_manage` needs `compliance.manage`: the checklist edit
// route is gated on COMPLIANCE_MANAGE and resolves COMPLIANCE_MASTER_MANAGE
// only as a *flag* once the row is loaded (compliance/router.py:1319,1327).
// Master-manage on its own therefore grants nothing at all — the edit 403s
// before the flag is ever read — so a role granted it alone would show an
// authority it does not have.
//
// Deliberately one-way. Manage does NOT pull in master_manage: a tenant lets
// recruiters build checklists while only a compliance lead changes the country
// sets those checklists start from, and that separation is the whole reason
// the second code exists.
const IMPLIED_BY_CODE = {
    "compliance.master_manage": ["compliance.manage"],
};

// Resources where a "manage" grant really does cover the granular actions —
// where the backend gates those routes on
// `require_any_permission(<action>, <resource>.manage)`, so a role holding
// manage alone can already perform them and ticking both is redundant.
//
// Keyed by RESOURCE rather than by action, because the answer differs per
// resource and an action name alone cannot carry it. `role` is here because
// roles/router.py:227,265,314 are three such gates.
//
// `compliance` is deliberately absent. COMPLIANCE_CREATE is the sole gate on
// Send-to-Compliance (compliance/router.py:233), and nothing in compliance
// implies anything else — IMPLIED_PERMISSIONS is interviews-only. Listing it
// here did not merely grey the checkbox: `withPermissionDependencies` stripped
// the code from the payload on save, so a role granted Manage lost
// Send-to-Compliance entirely, with nothing on screen to say so.
const MANAGE_SUBSUMES_BY_RESOURCE = {
    role: ["create", "update", "delete"],
};

// The catalogue rows carry `resource` (split server-side by _split_code), but
// fall back to the code's own prefix so a row from an older response still
// resolves.
const resourceOf = (perm) => perm.resource || String(perm.code || "").split(".")[0];

/** The actions `manage` subsumes for this permission's resource, if any. */
const subsumedActionsFor = (perm) => MANAGE_SUBSUMES_BY_RESOURCE[resourceOf(perm)] ?? [];

/**
 * Per-category (== per-resource) checkbox state: which permission codes
 * must be disabled right now, and why, given what's currently selected.
 */
export const getPermissionRuleState = (categoryPerms, selectedCodes) => {
    const byAction = Object.fromEntries(categoryPerms.map((p) => [p.action, p]));
    const viewPerm = byAction.view;
    const managePerm = byAction.manage;

    const hasView = viewPerm ? selectedCodes.includes(viewPerm.code) : true;
    const hasManage = managePerm ? selectedCodes.includes(managePerm.code) : false;

    const state = {};
    for (const p of categoryPerms) {
        if (viewPerm && p.code !== viewPerm.code && VIEW_DEPENDENT_ACTIONS.includes(p.action) && !hasView) {
            state[p.code] = { disabled: true, reason: `Requires "${viewPerm.displayName || viewPerm.code}" first` };
            continue;
        }
        if (managePerm && p.code !== managePerm.code && subsumedActionsFor(p).includes(p.action) && hasManage) {
            state[p.code] = { disabled: true, reason: `Already included by "${managePerm.displayName || managePerm.code}"` };
            continue;
        }
        state[p.code] = { disabled: false, reason: null };
    }
    return state;
};

/**
 * After a single-checkbox toggle, strip codes that are no longer valid —
 * e.g. unchecking "View" also drops Create/Update/Delete/... for that
 * resource, so the stored permission set never has an orphaned dependent.
 */
export const withPermissionDependencies = (categoryPerms, nextSelected) => {
    let sanitized = [...nextSelected];

    const viewPerm = categoryPerms.find((p) => p.action === "view");
    const managePerm = categoryPerms.find((p) => p.action === "manage");

    // Rule 1: without "view", remove dependent actions
    if (viewPerm && !sanitized.includes(viewPerm.code)) {
        const dependentCodes = categoryPerms
            .filter((p) => VIEW_DEPENDENT_ACTIONS.includes(p.action))
            .map((p) => p.code);

        sanitized = sanitized.filter((code) => !dependentCodes.includes(code));
    }

    // Rule 1b: a code that grants nothing without another one brings that other
    // one with it. Runs BEFORE the manage rules below, so an implied `manage`
    // is in the set by the time they read it — otherwise ticking master_manage
    // would add manage and then have the subsumption rules judge a set that
    // never contained it. Only codes this category actually offers are added,
    // so a category rendered without its `manage` row cannot gain a phantom.
    for (const code of [...sanitized]) {
        for (const implied of IMPLIED_BY_CODE[code] ?? []) {
            if (!sanitized.includes(implied) && categoryPerms.some((p) => p.code === implied)) {
                sanitized.push(implied);
            }
        }
    }

    // Rule 1c: the converse. Removing the code that made an implication hold
    // must not leave the dependent behind — unticking Manage drops
    // Master Manage, which without it opens no route.
    for (const [code, impliedCodes] of Object.entries(IMPLIED_BY_CODE)) {
        if (!sanitized.includes(code)) continue;
        const missing = impliedCodes.some((implied) => categoryPerms.some((p) => p.code === implied) && !sanitized.includes(implied));
        if (missing) sanitized = sanitized.filter((c) => c !== code);
    }

    // Rule 2: with "manage", remove granular actions already covered by it —
    // only for the resources where the backend really covers them. This rule
    // and `getPermissionRuleState` are two readings of one truth (one greys
    // the box, one strips the code), so they consult the same table: fixing
    // only one un-greys a checkbox whose permission still vanishes on save.
    if (managePerm && sanitized.includes(managePerm.code)) {
        const subsumedCodes = categoryPerms
            .filter((p) => subsumedActionsFor(p).includes(p.action))
            .map((p) => p.code);

        sanitized = sanitized.filter((code) => !subsumedCodes.includes(code));
    }

    return sanitized;
};

/**
 * "Select All" for one category, run through the same rule a single click is.
 *
 * The button used to write the raw union of the category's codes, which is the
 * one path around `withPermissionDependencies`: a category with a `manage`
 * code came out with Manage plus Create/Update/Delete all ticked, and
 * `getPermissionRuleState` then disabled those three as "Already included by
 * Manage" — ticked, redundant, and un-untickable without clearing Manage
 * first. Selections in other categories are left alone.
 */
export const withCategoryAllSelected = (categoryPerms, selectedCodes) => {
    const union = [
        ...new Set([...selectedCodes, ...categoryPerms.map((p) => p.code)]),
    ];

    return withPermissionDependencies(categoryPerms, union);
};

/** "Deselect All" for one category: drop its codes, keep every other one. */
export const withCategoryAllCleared = (categoryPerms, selectedCodes) => {
    const codes = categoryPerms.map((p) => p.code);

    return selectedCodes.filter((code) => !codes.includes(code));
};
